import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { featureFlagOverrideContract } from "@common/contracts/featureFlagOverride.contract";
import { organizationFeatureFlagOverrideContract } from "@common/contracts/organizationFeatureFlagOverride.contract";
import {
    FeatureFlagOverridesResponse,
    WorkspaceFeatureFlagOverridesResponse,
} from "@common/schemas/featureFlagOverride.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const featureFlagOverrideClient = getBackendAPIClient(
    featureFlagOverrideContract,
);

const organizationFeatureFlagOverrideClient = getBackendAPIClient(
    organizationFeatureFlagOverrideContract,
);

const featureFlagOverridesQueryKeys = {
    all: () => ["feature-flag-overrides"] as const,
    workspaces: () =>
        [...featureFlagOverridesQueryKeys.all(), "workspace"] as const,
    workspace: (workspaceId: string) =>
        [...featureFlagOverridesQueryKeys.workspaces(), workspaceId] as const,
    organization: (organizationId: string) =>
        [
            ...featureFlagOverridesQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
};

export function useFeatureFlagOverrides(
    workspaceId: string | null,
    options: { enabled: boolean },
) {
    return useQuery({
        queryKey: featureFlagOverridesQueryKeys.workspace(workspaceId ?? ""),
        queryFn: async (): Promise<WorkspaceFeatureFlagOverridesResponse> => {
            const response = await featureFlagOverrideClient.getOverrides({
                params: { workspaceId: workspaceId ?? "" },
            });
            if (response.status !== 200) {
                throw new Error("Failed to load feature flag overrides");
            }
            return response.body;
        },
        refetchOnWindowFocus: false,
        enabled: options.enabled && workspaceId !== null,
    });
}

export function useUpdateFeatureFlagOverrideMutation(workspaceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            flag,
            isEnabled,
        }: {
            flag: string;
            isEnabled: boolean | null;
        }): Promise<WorkspaceFeatureFlagOverridesResponse> => {
            const response =
                isEnabled === null
                    ? await featureFlagOverrideClient.deleteOverride({
                          params: { workspaceId, flag },
                      })
                    : await featureFlagOverrideClient.setOverride({
                          params: { workspaceId, flag },
                          body: { isEnabled },
                      });
            if (response.status !== 200) {
                throw new Error("Failed to update feature flag");
            }
            return response.body;
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                featureFlagOverridesQueryKeys.workspace(workspaceId),
                data,
            );
        },
    });
}

export function useResetFeatureFlagOverridesMutation(workspaceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn:
            async (): Promise<WorkspaceFeatureFlagOverridesResponse> => {
                const response =
                    await featureFlagOverrideClient.deleteAllOverrides({
                        params: { workspaceId },
                    });
                if (response.status !== 200) {
                    throw new Error("Failed to reset feature flags");
                }
                return response.body;
            },
        onSuccess: (data) => {
            queryClient.setQueryData(
                featureFlagOverridesQueryKeys.workspace(workspaceId),
                data,
            );
        },
    });
}

export function useOrganizationFeatureFlagOverrides(
    organizationId: string | null,
    options: { enabled: boolean },
) {
    return useQuery({
        queryKey: featureFlagOverridesQueryKeys.organization(
            organizationId ?? "",
        ),
        queryFn: async (): Promise<FeatureFlagOverridesResponse> => {
            const response =
                await organizationFeatureFlagOverrideClient.getOverrides({
                    params: { organizationId: organizationId ?? "" },
                });
            if (response.status !== 200) {
                throw new Error("Failed to load feature flag overrides");
            }
            return response.body;
        },
        refetchOnWindowFocus: false,
        enabled: options.enabled && organizationId !== null,
    });
}

/** Firm overrides are the default for every demo client workspace, so a change refetches the workspace overrides. */
const useOnOrganizationOverridesChanged = (organizationId: string) => {
    const queryClient = useQueryClient();

    return (data: FeatureFlagOverridesResponse) => {
        queryClient.setQueryData(
            featureFlagOverridesQueryKeys.organization(organizationId),
            data,
        );
        void queryClient.invalidateQueries({
            queryKey: featureFlagOverridesQueryKeys.workspaces(),
        });
    };
};

export function useUpdateOrganizationFeatureFlagOverrideMutation(
    organizationId: string,
) {
    const onSuccess = useOnOrganizationOverridesChanged(organizationId);

    return useMutation({
        mutationFn: async ({
            flag,
            isEnabled,
        }: {
            flag: string;
            isEnabled: boolean | null;
        }): Promise<FeatureFlagOverridesResponse> => {
            const response =
                isEnabled === null
                    ? await organizationFeatureFlagOverrideClient.deleteOverride(
                          { params: { organizationId, flag } },
                      )
                    : await organizationFeatureFlagOverrideClient.setOverride({
                          params: { organizationId, flag },
                          body: { isEnabled },
                      });
            if (response.status !== 200) {
                throw new Error("Failed to update feature flag");
            }
            return response.body;
        },
        onSuccess,
    });
}

export function useResetOrganizationFeatureFlagOverridesMutation(
    organizationId: string,
) {
    const onSuccess = useOnOrganizationOverridesChanged(organizationId);

    return useMutation({
        mutationFn: async (): Promise<FeatureFlagOverridesResponse> => {
            const response =
                await organizationFeatureFlagOverrideClient.deleteAllOverrides({
                    params: { organizationId },
                });
            if (response.status !== 200) {
                throw new Error("Failed to reset feature flags");
            }
            return response.body;
        },
        onSuccess,
    });
}
